import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { getInitials, formatHandicap } from '../utils/format';
import { useToast } from '../context/ToastContext';
import PostCard from '../components/PostCard';
import CourseAutocomplete from '../components/CourseAutocomplete';
import Icon from '../components/Icon';
import './Home.css';

function CreatePostModal({ onClose, onPost, initialType = 'text', initialCourse = '' }) {
  const { user } = useAuth();
  const [postType, setPostType] = useState(initialType);
  const [content, setContent] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [courseName, setCourseName] = useState(initialCourse);
  const [courseCity, setCourseCity] = useState('');
  const [courseState, setCourseState] = useState('');
  const [score, setScore] = useState('');
  const [scoreDiff, setScoreDiff] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const post = await api.createPost({
        postType,
        content: content || undefined,
        photoUrl: photoUrl || undefined,
        courseName: courseName || undefined,
        courseCity: courseCity || undefined,
        courseState: courseState || undefined,
        score: score ? parseInt(score) : undefined,
        scoreDifferential: scoreDiff ? parseFloat(scoreDiff) : undefined,
      });
      onPost(post);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>New Post</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close"><Icon name="x" size={20} /></button>
        </div>

        <div className="post-type-tabs">
          {[['text', 'chat', 'Update'], ['round_log', 'scorecard', 'Round'], ['photo', 'image', 'Photo']].map(([type, icon, label]) => (
            <button
              key={type}
              type="button"
              className={`post-type-tab ${postType === type ? 'active' : ''}`}
              onClick={() => setPostType(type)}
            ><Icon name={icon} size={16} /> {label}</button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {postType === 'round_log' && (
            <>
              <div className="form-group">
                <label className="form-label">Course</label>
                <CourseAutocomplete
                  value={courseName}
                  onChange={setCourseName}
                  onSelect={course => {
                    setCourseName(course.name);
                    setCourseCity(course.city || '');
                    setCourseState(course.state || '');
                  }}
                  inlineAdd
                  placeholder={user?.home_course || 'e.g. Pebble Beach Golf Links'}
                />
              </div>
              <div className="modal-row">
                <div className="form-group">
                  <label className="form-label">City</label>
                  <input className="form-input" value={courseCity} onChange={e => setCourseCity(e.target.value)} placeholder="Pebble Beach" />
                </div>
                <div className="form-group">
                  <label className="form-label">State</label>
                  <input className="form-input" value={courseState} onChange={e => setCourseState(e.target.value)} placeholder="CA" maxLength={2} />
                </div>
              </div>
              <div className="modal-row">
                <div className="form-group">
                  <label className="form-label">Score</label>
                  <input className="form-input" type="number" value={score} onChange={e => setScore(e.target.value)} placeholder="72" />
                </div>
                <div className="form-group">
                  <label className="form-label">Differential</label>
                  <input className="form-input" type="number" step="0.1" value={scoreDiff} onChange={e => setScoreDiff(e.target.value)} placeholder="14.2" />
                </div>
              </div>
            </>
          )}

          {postType === 'photo' && (
            <div className="form-group">
              <label className="form-label">Photo URL</label>
              <input className="form-input" value={photoUrl} onChange={e => setPhotoUrl(e.target.value)}
                placeholder="https://..." />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">
              {postType === 'round_log' ? 'Notes (optional)' : postType === 'photo' ? 'Caption' : 'What\'s on your mind?'}
            </label>
            <textarea className="form-input form-textarea" value={content} onChange={e => setContent(e.target.value)}
              placeholder={postType === 'round_log' ? 'Great round today...' : postType === 'photo' ? 'Beautiful day on the course...' : 'Share something with your golf network...'}
              rows={3} />
          </div>

          {postType === 'round_log' && (
            <div className="ghin-note">
              <Icon name="bulb" size={15} /> <strong>GHIN tip:</strong> Add your GHIN ID to your profile so your handicap stays accurate.
            </div>
          )}

          {error && <div className="form-error">{error}</div>}

          <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={submitting || (!content && !photoUrl && !courseName)}>
            {submitting ? 'Posting...' : 'Post'}
          </button>
        </form>
      </div>
    </div>
  );
}

function IdentityRail({ user, connectionsCount, roundsCount }) {
  return (
    <aside className="home-rail home-rail-left">
      <div className="identity-card card">
        <Link to="/profile" className="identity-main">
          <div className="avatar avatar-lg identity-avatar">
            {user?.photo_url ? <img src={user.photo_url} alt={user.name} /> : getInitials(user?.name || '')}
          </div>
          <p className="identity-name">{user?.name}</p>
          {user?.home_course && <p className="identity-course"><Icon name="flag" size={13} /> {user.home_course}</p>}
          {(user?.city || user?.state) && (
            <p className="identity-location">{user.city}{user.city && user.state ? ', ' : ''}{user.state}</p>
          )}
        </Link>
        <div className="identity-stats">
          <div className="identity-stat">
            <span className="identity-stat-value">
              {formatHandicap(user?.handicap) || '—'}
            </span>
            <span className="identity-stat-label">Handicap</span>
          </div>
          <Link to="/connections" className="identity-stat">
            <span className="identity-stat-value">{connectionsCount}</span>
            <span className="identity-stat-label">Connections</span>
          </Link>
          <Link to="/marketplace" className="identity-stat">
            <span className="identity-stat-value">{roundsCount}</span>
            <span className="identity-stat-label">Rounds</span>
          </Link>
        </div>
        <div className="identity-links">
          <Link to="/qr" className="identity-link"><Icon name="qr" size={16} /> My QR code</Link>
          <Link to="/marketplace" className="identity-link"><Icon name="calendar" size={16} /> Tee times</Link>
          <Link to="/search" className="identity-link"><Icon name="search" size={16} /> Find golfers</Link>
        </div>
      </div>
    </aside>
  );
}

function SuggestedGolfers({ suggestions, onConnect, sentIds }) {
  return (
    <aside className="home-rail home-rail-right">
      <div className="home-suggested-card card">
        <h3 className="home-suggested-title">Golfers you may know</h3>
        {suggestions.length === 0 ? (
          <div className="home-suggested-empty">
            <p>Grow your network to see suggestions here.</p>
            <Link to="/search" className="btn btn-secondary btn-sm">Find Golfers</Link>
          </div>
        ) : (
          <div className="home-suggested-list">
            {suggestions.map(g => (
              <div key={g.id} className="home-suggested-row">
                <Link to={`/golfer/${g.id}`} className="home-suggested-info">
                  <div className="avatar avatar-sm">
                    {g.photo_url ? <img src={g.photo_url} alt={g.name} /> : getInitials(g.name)}
                  </div>
                  <div className="home-suggested-text">
                    <p className="home-suggested-name">{g.name}</p>
                    <p className="home-suggested-meta">
                      {g.handicap != null && <span>Hdcp {formatHandicap(g.handicap)}</span>}
                      {g.handicap != null && g.mutual_count > 0 && ' · '}
                      {g.mutual_count > 0 && <span>{g.mutual_count} mutual</span>}
                    </p>
                  </div>
                </Link>
                {sentIds.has(g.id) ? (
                  <span className="home-suggested-sent"><Icon name="check" size={14} /> Sent</span>
                ) : (
                  <button className="home-suggested-connect-btn" onClick={() => onConnect(g.id)}>Connect</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}

function Home() {
  const { user } = useAuth();
  const toast = useToast();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  // "Log this round" after a QR connect lands here with the composer prefilled
  const [composer, setComposer] = useState(() => (
    location.state?.openPostModal
      ? { type: location.state.postType || 'round_log', course: location.state.prefillCourse || '' }
      : null
  ));
  const showCreatePost = composer !== null;
  const setShowCreatePost = (open) => setComposer(open ? { type: 'text', course: '' } : null);
  const [myRoundsCount, setMyRoundsCount] = useState(0);
  const [connectionsCount, setConnectionsCount] = useState(0);
  const [suggestions, setSuggestions] = useState([]);
  const [sentIds, setSentIds] = useState(new Set());

  // Drop the one-shot router state so a refresh doesn't reopen the composer
  useEffect(() => {
    if (location.state?.openPostModal) navigate(location.pathname, { replace: true, state: null });
  }, []);

  useEffect(() => {
    const load = async () => {
      const [feedData, myRounds, connections, suggested] = await Promise.allSettled([
        api.getFeed(20),
        api.getMyRounds(),
        api.getConnections(),
        api.getSuggestedUsers(),
      ]);
      if (feedData.status === 'fulfilled') setPosts(feedData.value);
      if (myRounds.status === 'fulfilled') setMyRoundsCount(myRounds.value.length);
      if (connections.status === 'fulfilled') setConnectionsCount(connections.value.length);
      if (suggested.status === 'fulfilled') setSuggestions(suggested.value.slice(0, 5));
      setLoading(false);
    };
    load();
  }, []);

  const handleNewPost = (post) => setPosts(prev => [post, ...prev]);

  const handleDelete = async (postId) => {
    try {
      await api.deletePost(postId);
      setPosts(prev => prev.filter(p => p.id !== postId));
    } catch { toast.error('Failed to delete post'); }
  };

  const handleConnect = async (userId) => {
    if (sentIds.has(userId)) return;
    // Optimistic: mark sent immediately so a double-click can't fire twice;
    // roll back if the request fails
    setSentIds(prev => new Set([...prev, userId]));
    try {
      await api.sendConnectionRequest(userId);
      toast.success('Connection request sent');
    } catch (err) {
      setSentIds(prev => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
      toast.error(err.message || 'Failed to send request');
    }
  };

  return (
    <div className="home-page container">
      <div className="home-grid">
        <IdentityRail user={user} connectionsCount={connectionsCount} roundsCount={myRoundsCount} />

        <main className="home-feed-col">
          <div className="home-greeting">
            <p className="eyebrow">{new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).replace(',', ' ·')}</p>
            <h1 className="home-greeting-title">Who are you playing with this week?</h1>
          </div>

          {/* Create Post */}
          <div className="create-post-bar card">
            <div className="avatar avatar-sm">
              {user?.photo_url ? <img src={user.photo_url} alt={user.name} /> : user?.name?.charAt(0).toUpperCase()}
            </div>
            <button className="create-post-prompt" onClick={() => setShowCreatePost(true)}>
              What's happening on the course, {user?.name?.split(' ')[0]}?
            </button>
            <div className="create-post-actions">
              <button className="create-post-action-btn" onClick={() => setComposer({ type: 'round_log', course: '' })}><Icon name="scorecard" size={15} /> Round</button>
              <button className="create-post-action-btn" onClick={() => setComposer({ type: 'photo', course: '' })}><Icon name="image" size={15} /> Photo</button>
            </div>
          </div>

          {/* Feed */}
          {loading ? (
            <div className="loading-container"><div className="spinner"></div></div>
          ) : posts.length > 0 ? (
            <div className="feed-list">
              {posts.map(post => (
                <PostCard
                  key={post.id}
                  post={post}
                  currentUserId={user?.id}
                  onDelete={handleDelete}
                  toast={toast}
                />
              ))}
            </div>
          ) : (
            <div className="empty-state card">
              <div className="empty-state-icon"><Icon name="flag" size={24} /></div>
              <p className="empty-state-title">Welcome to around!</p>
              <p className="empty-state-text">Your feed will show rounds, updates, and photos from golfers you connect with. Here's how to get started:</p>
              <ol className="empty-state-steps">
                <li><strong>Find golfers</strong> near you and send a connection request.</li>
                <li><strong>Share your first round</strong> to let your network know you're active.</li>
                <li><strong>Check the marketplace</strong> to find or post open tee times.</li>
              </ol>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 16 }}>
                <Link to="/search" className="btn btn-primary">Find Golfers</Link>
                <button className="btn btn-secondary" onClick={() => setComposer({ type: 'round_log', course: '' })}>Share a Round</button>
              </div>
            </div>
          )}
        </main>

        <SuggestedGolfers suggestions={suggestions} onConnect={handleConnect} sentIds={sentIds} />
      </div>

      {showCreatePost && (
        <CreatePostModal
          onClose={() => setShowCreatePost(false)}
          onPost={handleNewPost}
          initialType={composer.type}
          initialCourse={composer.course}
        />
      )}
    </div>
  );
}

export default Home;
